import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CollapsibleCard from "components/CollapsibleCard";
import { Box } from "@mui/system";
import { get, post, request } from "../../../../../requests";
import Alert from "@mui/material/Alert";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { useStoreActions, useStoreState } from "easy-peasy";
import moment from "moment";
import CollapsiblePivotTable from "lib/Tables/PivotTable/CollapsiblePivotTable";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { formatMonthYearToInput } from "utils/CalanderUtils";
import Loader from "components/Loader/Basic";
import OcrolusPivotTable from "./OcrolusPivotTable";

interface BankStatementsSummaryProps {
  dealApplicationId: any;
  onViewTransactions?: (accountData: { accountnumber: string; monthyear: string }) => void;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
  bankAccounts: any;
  debits: any;
  summaryMonths: any;
  loading: boolean;
}

const OcrolusStatementsSummary: React.FC<BankStatementsSummaryProps> = ({
  dealApplicationId,
  onViewTransactions,
  setComponentLoading,
  bankAccounts,
  debits,
  summaryMonths,
  loading,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [pendingEditRow, setPendingEditRow] = useState<any | null>(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showTransactionAlert, setShowTransactionAlert] = useState(false);
  const [showMultiSelectAlert, setShowMultiSelectAlert] = useState(false);
  const bankStatementsSummary = useStoreState(
    (state: any) => state.bankSummaryStore.bankStatementData
  );
  const setBankStatementsSummary = useStoreActions(
    (actions: any) => actions.bankSummaryStore.setBankStatementData
  );

  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [openAddDialog, setOpenAddDialog] = useState(false);

  const columns = [
    {
      Header: "ACC NUMBER",
      accessor: "accountNumber",
      align: "left",
      input: "text",
      width: "230px",
      required: true,
    },
    {
      Header: "MONTH",
      accessor: "month",
      width: "100px",
      align: "left",
      input: "monthYear",
      required: true,
    },
    {
      Header: "STARTING BAL.",
      accessor: "startingBalance",
      width: "100px",
      input: "amount",
      align: "left",
      required: true,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.original?.startingBalance),
    },
    {
      Header: "TOTAL CREDITS",
      accessor: "totalCredits",
      width: "120px",
      align: "left",
      input: "amount",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.original?.totalCredits),
    },
    {
      Header: "UNTRUE CREDITS",
      accessor: "untrueCredits",
      width: "100px",
      input: "amount",
      align: "left",
      required: false,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.original?.untrueCredits),
    },
    {
      Header: "TRUE CREDITS",
      accessor: "totalTrueCredits",
      align: "left",
      width: "100px",
      input: "amount",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.original?.totalTrueCredits),
    },
    {
      Header: "TOTAL MCA Debits",
      accessor: "totalMcaDebits",
      width: 120,
      input: "amount",
      align: "left",
      required: false,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.original?.totalMcaDebits),
    },
    {
      Header: "Expenses",
      accessor: "totalDebits",
      width: 120,
      input: "amount",
      align: "left",
      required: false,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.original?.totalDebits),
    },

    {
      Header: "DAILY BAL",
      accessor: "avgDailyBalance",
      align: "left",
      width: "100px",
      input: "amount",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.original?.avgDailyBalance),
    },
    {
      Header: "MCA HB %",
      accessor: "leveragedPercent",
      align: "left",
      width: "80px",
      input: "number",
      Cell: (row: any) => {
        const value = row?.row?.original?.leveragedPercent;
        if (value == null || isNaN(value)) return "-";
        return `${parseFloat(value).toFixed(2)}%`;
      },
    },
    {
      Header: "# DAYS NEG",
      accessor: "daysNegative",
      width: "80px",
      input: "number",
      required: false,
    },
    { Header: "# NSF", accessor: "nsfCount", width: "50px", input: "number", required: false },
  ];

  const getTotalDebits = (month: string, bankPk: string | number) =>
    debits?.[month]?.[bankPk]?.totalFintechDebits ?? 0;

  const fetchAndCombineData = async () => {
    try {
      setComponentLoading?.("bankStatements", true);

      const rows: any[] = [];
      const months = summaryMonths || {};

      Object.entries(months).forEach(([month, accounts]: any) => {
        Object.entries(accounts).forEach(([bankPk, acct]: any) => {
          const totalCredits = acct.totalCredits ?? 0;
          const trueCredits = acct.trueCredits ?? 0;
          const mcaDebits = acct.mcaDebits ?? 0;
          const totalDebits = getTotalDebits(month, bankPk);

          rows.push({
            id: `${bankPk}_${month}`,
            applicationId: dealApplicationId,
            accountNumber: acct.accountName || "-",
            month,
            startingBalance: acct.startingBalance ?? 0,
            totalCredits,
            untrueCredits: acct.untrueCredits ?? 0,
            totalTrueCredits: trueCredits,
            totalMcaDebits: totalDebits,
            avgDailyBalance: acct.avgDailyBalance ?? 0,
            leveragedPercent: trueCredits > 0 ? (totalDebits / trueCredits) * 100 : 0,
            daysNegative: acct.daysNegative ?? 0,
            totalDebits: acct.expenses?? 0,
            nsfCount: acct.nsfCount ?? 0,
            source: "Ocrolus",
            selected: true,
          });
        });
      });

      setBankStatementsSummary(rows);
      setSelectedRows(rows.map((r) => r.id));
    } catch (e) {
      console.error("Failed to load Ocrolus summary", e);
      setBankStatementsSummary([]);
      setSelectedRows([]);
    } finally {
      setComponentLoading?.("bankStatements", false);
    }
  };

  useEffect(() => {
    fetchAndCombineData();
  }, [dealApplicationId, summaryMonths]);

  const handleSaveRow = async (row: any) => {
    setIsSaving(true);
    try {
      if (row?.id && typeof row.id === "string" && row.id.startsWith("new")) {
        row.id = null;
      }

      if (!row.applicationId) {
        row.applicationId = dealApplicationId;
      }

      const payload = row;
      await post("bankStatements/saveOrUpdateFinancialReport", payload);
      fetchAndCombineData();
      setIsEditing(false);
      setEditingRow(null);
    } catch (error) {
      console.error("Error saving bank statement:", error);
    } finally {
      setIsSaving(false);
      setPendingEditRow(null);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingRow(null);
  };

  const handleInputChange = (value: any, field: string) => {
    setEditingRow((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditRow = (row: any) => {
    if (row.source?.toLowerCase() === "moneythumb" || row.source === "AI" || row.source == "OCR") {
      console.log(row);
      setPendingEditRow(row);
      setShowEditWarning(true);
      return;
    }
    setEditingRow({ ...row });
    setIsEditing(true);
  };

  const handleSort = (columnKey: string) => {
    if (sortedColumn === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortedColumn(columnKey);
      setSortDirection("asc");
    }
  };

  const handleSelectRow = (rowKey: any) => {
    const updated = bankStatementsSummary.map((row: any) => {
      if (row.id === rowKey) {
        return { ...row, selected: !row.selected };
      }
      return row;
    });

    setBankStatementsSummary(updated);

    const updatedSelectedRows = updated
      .filter((row: any) => row.selected)
      .map((row: any) => row.id);

    setSelectedRows(updatedSelectedRows);
  };

  const handleSelectAll = () => {
    const allSelected = bankStatementsSummary.every((row: any) => row.selected);

    const updated = bankStatementsSummary.map((row: any) => ({
      ...row,
      selected: !allSelected,
    }));

    setBankStatementsSummary(updated);

    const updatedSelectedRows = !allSelected ? updated.map((row: any) => row.id) : [];

    setSelectedRows(updatedSelectedRows);
  };

  const handleDeleteRow = async (id: string | number) => {
    try {
      const rowToDelete = bankStatementsSummary.find((row: any) => row?.id === id);

      if (!rowToDelete) {
        console.error("Row not found");
        return;
      }

      const restrictedSources = ["MoneyThumb", "AI", "OCR"];
      if (restrictedSources.includes(rowToDelete.source)) {
        setShowDeleteAlert(true);
        setPendingEditRow(rowToDelete);
        return;
      }

      if (rowToDelete.id) {
        await request("delete", `bankStatements/deleteFinancialReport/${rowToDelete.id}`);
        fetchAndCombineData();
      }
    } catch (error) {
      console.error("Error deleting bank summary:", error);
    }
  };

  const renderTotal = (rows: any[], key: string) => {
    let total = 0;

    if (key === "leveragedPercent") {
      let totalMcaDebits = 0;
      let totalTrueCredits = 0;

      rows
        .filter((row: any) => row.selected)
        .forEach((row: any) => {
          totalMcaDebits += parseFloat(row["totalMcaDebits"] || "0") || 0;
          totalTrueCredits += parseFloat(row["totalTrueCredits"] || "0") || 0;
        });

      total = totalTrueCredits ? (totalMcaDebits / totalTrueCredits) * 100 : 0;
      return `${total.toFixed(2)}%`;
    }

    total = rows
      .filter((row: any) => row.selected)
      .reduce((acc, row) => {
        const value = parseFloat(row[key] || "0");
        return acc + (isNaN(value) ? 0 : value);
      }, 0);

    if (key.includes("Amount")) {
      return formatCurrencyUSD(total.toFixed(2));
    }

    if (key.includes("Percent") || key.includes("leverage")) {
      return `${total.toFixed(2)}%`;
    }

    return formatCurrencyUSD(total.toFixed(2));
  };

  const getRowStyle = () => "regular";

  return (
    <CollapsibleCard header={"Bank Statements Summary"}>
      <>
        <MDBox paddingRight={3}>
          {loading ? (
            <Loader />
          ) : (
            <OcrolusPivotTable  
              columns={columns}
              data={bankStatementsSummary || []}
              rowKey="id"
              onDelete={undefined}
              onEdit={undefined}
              selectedRows={selectedRows}
              onSelectRow={handleSelectRow}
              onSelectAll={handleSelectAll}
              onSort={handleSort}
              sortedColumn={sortedColumn}
              sortDirection={sortDirection}
              getRowStyle={getRowStyle}
              renderTotal={renderTotal}
              totalColumn={[
                "totalCredits",
                "totalMcaCredits",
                "totalTrueCredits",
                "expenses",
                "totalMcaDebits",
                "leveragedPercent",
              ]}
              isActionsDisabled={() => false}
              groupBy="month"
              groupFormatter={(value: any) => {
                if (!value) return "Unknown";
                if (moment(value, moment.ISO_8601, true).isValid()) {
                  return moment(value).format("MMMM YYYY");
                }
                return String(value);
              }}
              defaultExpanded={false}
            />
          )}
        </MDBox>

        {showEditWarning && (
          <MDBox
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <MDBox
              sx={{
                backgroundColor: "background.paper",
                padding: 3,
                borderRadius: 1,
                maxWidth: 500,
              }}
            >
              <MDTypography variant="h6" mb={2}>
                <Alert variant="filled" severity="warning">
                  Warning
                </Alert>
              </MDTypography>
              <MDTypography mb={2}>
                {(pendingEditRow?.source === "AI" ||
                  pendingEditRow?.source?.toLowerCase() === "ocr" ||
                  pendingEditRow?.source?.toLowerCase() === "moneythumb") && (
                  <>
                    <strong>Warning:</strong> You are about to manually edit this data. This may
                    cause misalignment with automatically derived transactions from{" "}
                    <em>{pendingEditRow?.source}</em>.
                    <br />
                    <br />
                  </>
                )}
                Do you still want to continue?
              </MDTypography>
              <MDBox display="flex" justifyContent="flex-end" gap={2}>
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (pendingEditRow) {
                      setEditingRow({ ...pendingEditRow });
                      setIsEditing(true);
                    }
                    setShowEditWarning(false);
                  }}
                >
                  Yes, Continue
                </MDButton>

                <MDButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setPendingEditRow(null);
                    setShowEditWarning(false);
                  }}
                >
                  No, Cancel
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        )}

        {showDeleteAlert && (
          <MDBox
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
            onClick={() => setShowDeleteAlert(false)}
          >
            <MDBox
              sx={{
                backgroundColor: "background.paper",
                borderRadius: 2,
                maxWidth: 600,
                textAlign: "center",
                border: "3px solid",
                borderColor: "gray",
                boxShadow: 24,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MDTypography variant="h6" mb={2}>
                <Alert
                  severity="info"
                  sx={{
                    "& .MuiAlert-message": {
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                      marginTop: "1rem",
                    },
                  }}
                >
                  {`Are you sure you want to delete this statement summary from ${
                    pendingEditRow?.source === "AI" || pendingEditRow?.source === "OCR"
                      ? "OCR"
                      : "Money Thumb"
                  }`}

                  <MDBox display="flex" justifyContent="center" gap={2} marginTop={2}>
                    <MDButton
                      variant="contained"
                      color="error"
                      onClick={async () => {
                        if (pendingEditRow.id) {
                          await request(
                            "delete",
                            `bankStatements/deleteFinancialReport/${pendingEditRow.id}`
                          );
                          fetchAndCombineData();
                        }
                        setShowDeleteAlert(false);
                        setPendingEditRow(null);
                      }}
                      sx={{
                        marginTop: "1rem",
                        fontSize: "12px",
                      }}
                    >
                      Delete
                    </MDButton>

                    <MDButton
                      variant="text"
                      color="secondary"
                      onClick={() => {
                        setShowDeleteAlert(false);
                        setPendingEditRow(null);
                      }}
                      sx={{
                        marginTop: "1rem",
                        fontSize: "12px",
                      }}
                    >
                      Cancel
                    </MDButton>
                  </MDBox>
                </Alert>
              </MDTypography>
            </MDBox>
          </MDBox>
        )}

        {showTransactionAlert && (
          <MDBox
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
            onClick={() => setShowTransactionAlert(false)}
          >
            <MDBox
              sx={{
                backgroundColor: "background.paper",
                borderRadius: 2,
                maxWidth: 400,
                textAlign: "center",
                border: "3px solid",
                borderColor: "gray",
                boxShadow: 24,
                p: 3,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Alert
                severity="info"
                sx={{
                  "& .MuiAlert-message": {
                    fontSize: "1.3rem",
                    fontWeight: "bold",
                    marginTop: "1rem",
                  },
                }}
              >
                {pendingEditRow?.source === "AI"
                  ? "Sorry, AI-generated statements don't have transaction-level data available."
                  : "Sorry, No transactions available for this manually updated statement"}
                <MDBox mt={2}>
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={() => setShowTransactionAlert(false)}
                    sx={{
                      width: "50px",
                      height: "20px",
                      marginTop: "1rem",
                      marginRight: "3.5rem",
                      fontSize: "1.2rem",
                    }}
                  >
                    OK
                  </MDButton>
                </MDBox>
              </Alert>
            </MDBox>
          </MDBox>
        )}

        {showMultiSelectAlert && (
          <MDBox
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
            onClick={() => setShowMultiSelectAlert(false)}
          >
            <MDBox
              sx={{
                backgroundColor: "background.paper",
                padding: 3,
                borderRadius: 1,
                maxWidth: 400,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please select only one account at a time to view transactions
              </Alert>
              <MDBox display="flex" justifyContent="flex-end">
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={() => setShowMultiSelectAlert(false)}
                >
                  OK
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        )}

        <Dialog
          open={openAddDialog}
          onClose={() => {
            setOpenAddDialog(false);
            setEditingRow(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Bank Statement Summary</DialogTitle>
          <DialogContent>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={{ padding: "10px", width: "30%" }}>
                    <strong>Account Number</strong>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <TextField
                      fullWidth
                      value={editingRow?.accountNumber || ""}
                      onChange={(e) => handleInputChange(e.target.value, "accountNumber")}
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: "10px" }}>
                    <strong>Month</strong>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <TextField
                      fullWidth
                      type="month"
                      value={formatMonthYearToInput(editingRow?.month)}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value) {
                          handleInputChange("", "month");
                          return;
                        }
                        const dt = moment(value, "YYYY-MM", true);
                        if (dt.isValid()) {
                          const formatted = dt.format("MMMM YYYY");
                          handleInputChange(formatted, "month");
                        } else {
                          handleInputChange("", "month");
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: "10px" }}>
                    <strong>Starting Balance</strong>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <TextField
                      fullWidth
                      type="number"
                      value={editingRow?.startingBalance || ""}
                      onChange={(e) => handleInputChange(Number(e.target.value), "startingBalance")}
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: "10px" }}>
                    <strong>Total Credits</strong>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <TextField
                      fullWidth
                      type="number"
                      value={editingRow?.totalCredits || ""}
                      onChange={(e) => handleInputChange(Number(e.target.value), "totalCredits")}
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: "10px" }}>
                    <strong>Total MCA Credits</strong>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <TextField
                      fullWidth
                      type="number"
                      value={editingRow?.totalMcaCredits || ""}
                      onChange={(e: any) =>
                        handleInputChange(Number(e.target.value), "totalMcaCredits")
                      }
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: "10px" }}>
                    <strong>True Credits</strong>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <TextField
                      fullWidth
                      type="number"
                      value={editingRow?.totalTrueCredits || ""}
                      onChange={(e) =>
                        handleInputChange(Number(e.target.value), "totalTrueCredits")
                      }
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: "10px" }}>
                    <strong>Total MCA Debits</strong>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <TextField
                      fullWidth
                      type="number"
                      value={editingRow?.totalMcaDebits || ""}
                      onChange={(e) => handleInputChange(Number(e.target.value), "totalMcaDebits")}
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: "10px" }}>
                    <strong>MCA Holdback %</strong>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <TextField
                      fullWidth
                      type="number"
                      value={editingRow?.leveragedPercent || ""}
                      onChange={(e) =>
                        handleInputChange(Number(e.target.value), "leveragedPercent")
                      }
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </DialogContent>

          <DialogActions>
            <MDButton
              onClick={() => {
                setOpenAddDialog(false);
                handleSaveRow(editingRow);
              }}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </MDButton>
            <MDButton
              onClick={() => {
                setOpenAddDialog(false);
                setEditingRow(null);
                setIsEditing(false);
              }}
              variant="outlined"
              color="secondary"
            >
              Cancel
            </MDButton>
          </DialogActions>
        </Dialog>
      </>
    </CollapsibleCard>
  );
};
export default OcrolusStatementsSummary;
